import { useEffect } from "react";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";

/**
 * useHeartbeat
 * Pings POST /api/users/heartbeat every 60s while:
 *  - The user is authenticated
 *  - The browser tab is visible
 *  - The browser is online
 * Powers the "is this hobby farmer online right now?" indicator across the app.
 */
export default function useHeartbeat() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    let timer = null;
    let cancelled = false;

    const ping = async () => {
      if (cancelled) return;
      if (typeof document !== "undefined" && document.visibilityState !== "visible") return;
      if (typeof navigator !== "undefined" && navigator.onLine === false) return;
      try { await api.post("/users/heartbeat"); } catch { /* silent — best-effort */ }
    };

    // Ping immediately, then every 60s
    ping();
    timer = setInterval(ping, 60_000);

    const onVisibility = () => { if (document.visibilityState === "visible") ping(); };
    document.addEventListener("visibilitychange", onVisibility);

    return () => {
      cancelled = true;
      if (timer) clearInterval(timer);
      document.removeEventListener("visibilitychange", onVisibility);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user?.user_id]);
}
